import React, {useCallback, useEffect, useMemo, useState} from 'react'
import {
    flexRender,
    getCoreRowModel,
    getSortedRowModel,
    SortingState,
    useReactTable,
} from '@tanstack/react-table'
import moment from 'moment'
import {useNavigate} from 'react-router-dom'
import {ToastContainer} from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import {Modal, ModalBody, ModalHeader} from 'react-bootstrap'
import {KTCard, KTCardBody, KTIcon} from '../../../_websoft/helpers'
import {MenuComponent} from '../../../_websoft/assets/ts/components'
import TablePagination from '../../components/TablePagination/TablePagination'
import TableSearch from '../../components/TableSearch/TableSearch'
import LoadingComponent from '../../components/LoadingComponent/LoadingComponent'
import notifyToast from '../../components/Toast/Toast'
import ArtikliGalanterija from '../../components/ArtikliGalanterija/ArtikliGalanterija'
import {PaginationModel} from '../../models/PaginationModel'
import {
    getPlaviNalogStatus,
    PLAVI_NALOG_STATUSI,
    PlaviNalog,
    PlaviNalogParams,
    STATUS_ZAVRSEN,
} from '../../models/PlaviNalog'
import {
    deletePlaviNalog,
    getAll,
    operacije as getOperacije,
    ulazArtikla,
} from '../../api/plaviNalozi'
import PlaviNalogForm from './PlaviNalogForm'

const formatDate = (date?: string) => (date ? moment(date).format('DD-MM-YYYY') : '-')

const getArtikalName = (row: PlaviNalog) => row.artikal?.ArtNaziv ?? row.artikal?.Naziv ?? '-'

const getKontrolisaoName = (row: PlaviNalog) =>
    row.kontrolisao?.UposlenikImePrezime ??
    row.nalogKontrolisaoUposlenik?.UposlenikImePrezime ??
    row.uposlenikKontrolisao?.UposlenikImePrezime

const StatusBadge = ({status}: {status?: number}) => {
    const statusInfo = getPlaviNalogStatus(status)
    return <span className={`badge badge-light-${statusInfo.badge}`}>{statusInfo.label}</span>
}

const toNumber = (value?: number | string) => {
    if (value === undefined || value === null || value === '') {
        return undefined
    }

    const numberValue = Number(value)

    return Number.isFinite(numberValue) ? numberValue : undefined
}

const getFinishedGoods = (row: PlaviNalog) => {
    if (!row.operacije?.length) {
        return undefined
    }

    return Math.min(...row.operacije.map((operation) => toNumber(operation.zavrsenaKolicina) ?? 0))
}

const getProgress = (row: PlaviNalog) => {
    const plannedQuantity = toNumber(row.kolicina) ?? toNumber(row.planiranaKolicina)
    const finishedGoods = getFinishedGoods(row)

    if (!plannedQuantity || finishedGoods === undefined) {
        return undefined
    }

    return Math.min(100, Math.max(0, Math.round((finishedGoods / plannedQuantity) * 100)))
}

const getProgressColor = (progress: number) => {
    if (progress < 50) {
        return 'danger'
    }

    if (progress <= 80) {
        return 'warning'
    }

    return 'success'
}

const ProgressBar = ({row}: {row: PlaviNalog}) => {
    const progress = getProgress(row)

    if (progress === undefined) {
        return <span className='text-muted fw-semibold'>Nema podataka</span>
    }

    const color = getProgressColor(progress)

    return (
        <div className='min-w-150px'>
            <div className='d-flex justify-content-between mb-1'>
                <span className={`fw-bold text-${color}`}>{progress}%</span>
            </div>
            <div className='progress h-8px bg-light'>
                <div
                    className={`progress-bar bg-${color}`}
                    role='progressbar'
                    style={{width: `${progress}%`}}
                    aria-valuenow={progress}
                    aria-valuemin={0}
                    aria-valuemax={100}
                />
            </div>
        </div>
    )
}

const PlaviNalogLista: React.FC = () => {
    const navigate = useNavigate()
    const [data, setData] = useState<PlaviNalog[]>([])
    const [loading, setLoading] = useState(true)
    const [sorting, setSorting] = useState<SortingState>([])
    const [formOpen, setFormOpen] = useState(false)
    const [editId, setEditId] = useState<number | null>(null)
    const [deleteId, setDeleteId] = useState<number | null>(null)
    const [ulazNalog, setUlazNalog] = useState<PlaviNalog | null>(null)
    const [ulazTip, setUlazTip] = useState<'protocni' | 'paletno' | ''>('')
    const [ulazLokacija, setUlazLokacija] = useState('')
    const [ulazLoading, setUlazLoading] = useState(false)
    const [pagination, setPagination] = useState<PaginationModel>({
        curPage: 1,
        totalPage: 1,
        perPage: 10,
    })
    const [params, setParams] = useState<PlaviNalogParams>({
        per_page: 10,
        page: 1,
    })

    const updatePage = useCallback(() => {
        setLoading(true)
        getAll(params)
            .then(async (response) => {
                const responseData = response.data as any
                const paginator = responseData?.data?.data && Array.isArray(responseData.data.data)
                    ? responseData.data
                    : responseData
                const rows = paginator?.data ?? []
                const safeRows = Array.isArray(rows) ? rows : []
                const total = paginator?.total ?? rows.length
                const perPage = paginator?.per_page ?? params.per_page
                const rowsWithOperations = await Promise.all(
                    safeRows.map(async (row: PlaviNalog) => {
                        if (row.operacije?.length) {
                            return row
                        }

                        try {
                            const operacije = await getOperacije(row.id)

                            return {
                                ...row,
                                operacije: Array.isArray(operacije) ? operacije : [],
                            }
                        } catch {
                            return row
                        }
                    })
                )

                setData(rowsWithOperations)
                setPagination({
                    curPage: paginator?.current_page ?? params.page,
                    totalPage: paginator?.last_page ?? Math.max(1, Math.ceil(total / perPage)),
                    perPage: perPage,
                })
            })
            .catch((error: any) => {
                notifyToast(false, error?.response?.data?.message ?? error?.message ?? 'Greška prilikom učitavanja.')
            })
            .finally(() => setLoading(false))
    }, [params])

    useEffect(() => {
        updatePage()
    }, [updatePage])

    useEffect(() => {
        MenuComponent.reinitialization()
    }, [data])

    const onCreate = () => {
        setEditId(null)
        setFormOpen(true)
    }

    const onEdit = (id: number) => {
        setEditId(id)
        setFormOpen(true)
    }

    const onDelete = () => {
        if (!deleteId) {
            return
        }

        deletePlaviNalog(deleteId)
            .then((response) => {
                notifyToast(true, response?.message ?? 'Nalog je obrisan.')
                setDeleteId(null)
                updatePage()
            })
            .catch((error: any) => notifyToast(false, error?.response?.data?.message ?? error?.message))
    }

    const onKreirajUlaz = () => {
        if (!ulazNalog || !ulazTip) {
            return
        }

        if (ulazTip === 'paletno' && !ulazLokacija.trim()) {
            notifyToast(false, 'Lokacija je obavezna za paletno skladište.')
            return
        }

        setUlazLoading(true)
        ulazArtikla(ulazNalog.id, ulazTip, ulazTip === 'paletno' ? ulazLokacija : undefined)
            .then((response) => {
                notifyToast(true, response?.message ?? 'Ulaz artikla je uspješno kreiran.')
                setUlazNalog(null)
                setUlazTip('')
                setUlazLokacija('')
                updatePage()
            })
            .catch((error: any) => notifyToast(false, error?.response?.data?.message ?? error?.message))
            .finally(() => setUlazLoading(false))
    }

    const columns = useMemo(
        () => [
            {
                id: 'id',
                header: 'Id',
                accessorKey: 'id',
            },
            {
                id: 'brojNaloga',
                header: 'Broj naloga',
                accessorKey: 'brojNaloga',
            },
            {
                id: 'artikal',
                header: 'Artikal',
                accessorFn: (row: PlaviNalog) => getArtikalName(row),
            },
            {
                id: 'kolicina',
                header: 'Količina',
                accessorKey: 'kolicina',
            },
            {
                id: 'planiraniPocetak',
                header: 'Planirani početak',
                accessorFn: (row: PlaviNalog) => formatDate(row.planiraniPocetak),
            },
            {
                id: 'planiraniKraj',
                header: 'Planirani kraj',
                accessorFn: (row: PlaviNalog) => formatDate(row.planiraniKraj),
            },
            {
                id: 'status',
                header: 'Status',
                cell: ({row}: any) => <StatusBadge status={row.original.status} />,
            },
            {
                id: 'progress',
                header: 'Progres',
                cell: ({row}: any) => <ProgressBar row={row.original} />,
            },
            {
                id: 'kontrola',
                header: 'Kontrola',
                cell: ({row}: any) => {
                    const kontrolisao = getKontrolisaoName(row.original)

                    if (!kontrolisao) {
                        return <span className='text-muted fw-semibold'>Nije kontrolisano</span>
                    }

                    return (
                        <div className='d-flex flex-column gap-2'>
                            <div className='d-flex align-items-center gap-2'>
                                <KTIcon
                                    iconName={row.original.nalogUredan ? 'check-circle' : 'cross-circle'}
                                    className={`fs-2 text-${row.original.nalogUredan ? 'success' : 'danger'}`}
                                />
                                <span className='fw-semibold'>{kontrolisao}</span>
                            </div>
                            {row.original.ulazArtiklaId ? (
                                <span className='badge badge-light-primary fw-bold'>
                                    Id ulaza: {row.original.ulazArtiklaId}
                                </span>
                            ) : row.original.status === STATUS_ZAVRSEN && row.original.nalogUredan && (
                                <button
                                    type='button'
                                    className='btn btn-sm btn-light-success'
                                    onClick={() => setUlazNalog(row.original)}
                                >
                                    Kreiraj ulaz
                                </button>
                            )}
                        </div>
                    )
                },
            },
            {
                id: 'analitika',
                header: 'Analitika',
                cell: ({row}: any) => (
                    <button
                        type='button'
                        className='btn btn-sm btn-light-info'
                        onClick={() => navigate(`/galanterija/plavi-nalog/${row.original.id}/analitika`)}
                    >
                        <KTIcon iconName='chart-simple' className='fs-3' />
                        Analitika
                    </button>
                ),
            },
            {
                id: 'actions',
                header: 'Actions',
                cell: ({row}: any) => (
                    <>
                        <button
                            className='btn btn-light btn-active-light-primary btn-sm'
                            data-kt-menu-trigger='click'
                            data-kt-menu-placement='bottom-end'
                        >
                            Actions
                            <KTIcon iconName='down' className='fs-5 m-0' />
                        </button>
                        <div
                            className='menu menu-sub menu-sub-dropdown menu-column menu-rounded menu-gray-600 menu-state-bg-light-primary fw-bold fs-7 w-150px py-4'
                            data-kt-menu='true'
                        >
                            <div className='menu-item px-3'>
                                <button
                                    type='button'
                                    className='menu-link px-3 btn btn-flush w-100 text-start'
                                    onClick={() => navigate(`/galanterija/plavi-nalog/${row.original.id}`)}
                                >
                                    View
                                </button>
                            </div>
                            <div className='menu-item px-3'>
                                <button
                                    type='button'
                                    className='menu-link px-3 btn btn-flush w-100 text-start'
                                    onClick={() => onEdit(row.original.id)}
                                >
                                    Edit
                                </button>
                            </div>
                            <div className='menu-item px-3'>
                                <button
                                    type='button'
                                    className='menu-link px-3 btn btn-flush w-100 text-start'
                                    onClick={() => setDeleteId(row.original.id)}
                                >
                                    Delete
                                </button>
                            </div>
                        </div>
                    </>
                ),
            },
        ],
        [navigate]
    )

    const table = useReactTable({
        data: data,
        columns,
        state: {
            sorting: sorting,
        },
        onSortingChange: setSorting,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
    })

    return (
        <KTCard>
            <ToastContainer />
            <PlaviNalogForm
                show={formOpen}
                id={editId}
                onHide={() => setFormOpen(false)}
                onSaved={updatePage}
            />
            <Modal show={Boolean(deleteId)} onHide={() => setDeleteId(null)}>
                <ModalHeader closeButton={true} className='h3 bg-warning'>
                    Da li želite obrisati nalog?
                </ModalHeader>
                <ModalBody className='d-flex justify-content-between'>
                    <button className='btn btn-secondary' onClick={() => setDeleteId(null)}>
                        NE
                    </button>
                    <button className='btn btn-primary' onClick={onDelete}>
                        DA
                    </button>
                </ModalBody>
            </Modal>
            <Modal
                show={Boolean(ulazNalog)}
                onHide={() => {
                    setUlazNalog(null)
                    setUlazTip('')
                    setUlazLokacija('')
                }}
            >
                <ModalHeader closeButton={true} className='h3'>
                    Kreiraj ulaz
                </ModalHeader>
                <ModalBody>
                    <div className='mb-6'>
                        <div className='fw-bold text-gray-800'>{ulazNalog?.brojNaloga}</div>
                        <div className='text-muted'>Odaberite tip ulaza.</div>
                    </div>
                    <div className='d-flex flex-column flex-md-row gap-4'>
                        <button
                            type='button'
                            className={`btn flex-fill ${ulazTip === 'protocni' ? 'btn-primary' : 'btn-light-primary'}`}
                            onClick={() => setUlazTip('protocni')}
                        >
                            Protocni Magacin
                        </button>
                        <button
                            type='button'
                            className={`btn flex-fill ${ulazTip === 'paletno' ? 'btn-success' : 'btn-light-success'}`}
                            onClick={() => setUlazTip('paletno')}
                        >
                            Paletno skladiste
                        </button>
                    </div>
                            {ulazTip && (
                        <div className='mt-6'>
                            <div className='alert alert-primary mb-5'>
                                {ulazTip === 'protocni'
                                    ? 'Odabrali ste ulaz za protocni magacin.'
                                    : 'Odabrali ste ulaz za paletno skladiste.'}
                            </div>
                            {ulazTip === 'paletno' && (
                                <div className='mb-5'>
                                    <label className='form-label required fw-bold'>Lokacija</label>
                                    <input
                                        type='text'
                                        className='form-control form-control-solid'
                                        value={ulazLokacija}
                                        onChange={(event) => setUlazLokacija(event.target.value)}
                                    />
                                </div>
                            )}
                            <button
                                type='button'
                                className='btn btn-primary w-100'
                                disabled={ulazLoading}
                                onClick={onKreirajUlaz}
                            >
                                Kreiraj ulaz
                            </button>
                            <div className='text-muted fw-semibold mt-3'>
                                Nakon sto se kreira ulaz plavi nalog ce biti arhiviran.
                            </div>
                        </div>
                    )}
                </ModalBody>
            </Modal>
            <KTCardBody className='py-4'>
                <div className='d-flex flex-column flex-lg-row justify-content-between gap-5 mb-8'>
                    <div>
                        <h2 className='mb-1'>Plavi nalozi</h2>
                        <div className='text-muted fw-semibold'>Production</div>
                    </div>
                    <div>
                        <button type='button' className='btn btn-primary' onClick={onCreate}>
                            <KTIcon iconName='plus' className='fs-2' />
                            Novi nalog
                        </button>
                    </div>
                </div>

                <div className='row g-5 mb-8'>
                    <div className='col-lg-4'>
                        <TableSearch
                            onSearch={(text) => setParams({...params, brojNaloga: text, page: 1})}
                        />
                    </div>
                    <div className='col-lg-3'>
                        <select
                            className='form-select form-select-solid'
                            value={params.status ?? ''}
                            onChange={(event) => setParams({...params, status: event.target.value, page: 1})}
                        >
                            <option value=''>Svi statusi</option>
                            {PLAVI_NALOG_STATUSI.map((status) => (
                                <option key={status.value} value={status.value}>
                                    {status.label}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className='col-lg-4'>
                        <ArtikliGalanterija
                            value={params.idArtikal}
                            selectName='idArtikal'
                            onChange={(name, value) => setParams({...params, idArtikal: value, page: 1})}
                        />
                    </div>
                </div>

                {loading ? (
                    <LoadingComponent />
                ) : (
                    <div className='table-responsive mt-3'>
                        <table className='table align-middle table-bordered gy-5 no-footer'>
                            <thead>
                                {table.getHeaderGroups().map((headerGroup) => (
                                    <tr
                                        key={headerGroup.id}
                                        className='text-start fw-bolder fs-7 text-uppercase gs-0 bg-dark p-2'
                                    >
                                        {headerGroup.headers.map((header) => (
                                            <th
                                                className='text-white'
                                                key={header.id}
                                                onClick={header.column.getToggleSortingHandler()}
                                            >
                                                {flexRender(header.column.columnDef.header, header.getContext())}
                                                {{
                                                    asc: '▲',
                                                    desc: '▼',
                                                }[header.column.getIsSorted() as string] ?? ''}
                                            </th>
                                        ))}
                                    </tr>
                                ))}
                            </thead>
                            <tbody className='text-gray-600'>
                                {table.getRowModel().rows.length > 0 ? (
                                    table.getRowModel().rows.map((row) => (
                                        <tr key={row.id}>
                                            {row.getVisibleCells().map((cell) => (
                                                <td key={cell.id}>
                                                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                </td>
                                            ))}
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan={columns.length}>
                                            <div className='d-flex text-center w-100 align-content-center justify-content-center'>
                                                No matching records found
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
                <TablePagination
                    updatePage={(page, per_page) => setParams({...params, per_page: per_page, page: page})}
                    pagination={pagination}
                    isLoading={loading}
                />
            </KTCardBody>
        </KTCard>
    )
}

export default PlaviNalogLista
